import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import Logo from '../components/Logo.jsx';

/**
 * ResetPassword
 * --------------
 * Lands here from the email-reset link: /reset-password?token=<resetToken>
 * Shows a "set a new password" form, calls /auth/forgot/reset, and signs the
 * user in on success.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [missingToken, setMissingToken] = useState(false);
  const { setSession } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) setMissingToken(true);
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const r = await apiPost('/auth/forgot/reset', { resetToken: token, password });
      if (!r?.token) throw new Error(r?.error || 'Reset failed');
      setSession(r.token, r.agent);
      toast.success('Password updated — signing you in');
      setTimeout(() => nav('/', { replace: true }), 50);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Reset failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-civix-bg relative overflow-hidden">
      <div className="absolute inset-0 bg-civix-radial pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-civix-purple/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-civix-cyan/20 rounded-full blur-3xl" />

      <div className="panel w-full max-w-md p-8 md:p-10 relative">
        <div className="mb-6"><Logo size="md" /></div>
        <h1 className="text-2xl font-display font-bold mb-1">Set a new password</h1>
        <p className="text-white/50 mb-6 text-sm">Choose a new password for your CIVIX account.</p>

        {missingToken ? (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200">
              This reset link is missing or invalid. Request a new one from the forgot-password page.
            </div>
            <Link to="/forgot-password" className="btn-primary w-full block text-center">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">New password</label>
              <input type="password" minLength={6} className="input mt-1" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <div>
              <label className="label">Confirm password</label>
              <input type="password" minLength={6} className="input mt-1" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
              {loading ? 'Saving…' : 'Save & sign in'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-white/50">
          <Link to="/login" className="text-civix-purpleSoft hover:text-civix-cyan">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
